import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CoreDataService } from '@core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type Alert = {
  title?: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  dismissible: boolean;
  resolveButton?: { text: string; link: string[] };
};

export const runtimeAlerts: { [key: string]: Alert } = {
  contractWithoutLicensePlateAlert: {
    message: 'Alert.ContractWithoutLicensePlateDescription',
    type: 'error',
    dismissible: true,
  },
  parkingSessionCouldNotBeTransferredAlert: {
    message: 'SignUp.ParkingSessionCouldNotBeTransferredError',
    type: 'error',
    dismissible: true,
  },
};

export const coreDataAlerts: { [key: string]: Alert } = {
  paymentMethodAlert: {
    message: 'Alert.AddPaymentMethodDescription',
    type: 'error',
    dismissible: false,
  },
  customerAddressAlert: {
    message: 'Alert.AddCustomerAddressDescription',
    type: 'error',
    dismissible: false,
  },
  paymentMethodAndCustomerAddressAlert: {
    message: 'Alert.CompleteProfileDescription',
    type: 'error',
    dismissible: false,
  },
  paymentFailedAlert: {
    message: 'Alert.PaymentFailedDescription',
    type: 'error',
    dismissible: false,
  },
  banImminentAlert: {
    message: 'Alert.BanImminentDescription',
    type: 'error',
    dismissible: false,
  },
  missingPaymentMethodAlert: {
    message: 'Alert.MissingPaymentMethodDescription',
    type: 'error',
    dismissible: false,
  },
  bannedAlert: {
    message: 'Alert.BannedDescription',
    type: 'error',
    dismissible: false,
  },
};

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  alertsSubject = new BehaviorSubject<Alert[]>([]);
  private destroyRef = inject(DestroyRef);

  constructor(private coreDataService: CoreDataService) {
    this.coreDataService.warningData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((warningData) => {
      this.removeAllCoreDataAlerts();
      if (warningData.banStatus) {
        if (warningData.banStatus.active) {
          if (warningData.banStatus.reason === 'due_payment') {
            this.add(coreDataAlerts['paymentFailedAlert']);
          } else if (warningData.banStatus.reason === 'missing_payment_method') {
            this.add(coreDataAlerts['missingPaymentMethodAlert']);
          } else if (warningData.banStatus.reason === 'other') {
            this.add(coreDataAlerts['bannedAlert']);
          }
        } else {
          this.add(coreDataAlerts['banImminentAlert']);
        }
      } else if (warningData.paymentMissing && warningData.customerAddressMissing) {
        this.add(coreDataAlerts['paymentMethodAndCustomerAddressAlert']);
      } else if (warningData.paymentMissing) {
        this.add(coreDataAlerts['paymentMethodAlert']);
      } else if (warningData.customerAddressMissing) {
        this.add(coreDataAlerts['customerAddressAlert']);
      }
    });
  }

  add(alert: Alert) {
    const alertExists = this.alertsSubject.value.some(
      (existingAlert) => existingAlert.message === alert.message && existingAlert.type === alert.type,
    );

    if (!alertExists) {
      this.alertsSubject.next([...this.alertsSubject.value, alert]);
    }
  }

  removeAlert(alert: Alert) {
    this.alertsSubject.next(this.alertsSubject.value.filter((a) => a !== alert));
  }

  removeAllCoreDataAlerts() {
    const coreAlertSet = new Set(Object.values(coreDataAlerts));
    this.alertsSubject.next(this.alertsSubject.value.filter((alert) => !coreAlertSet.has(alert)));
  }

  removeAll() {
    this.alertsSubject.next([]);
  }
}
